import { Injectable } from '@angular/core';
import { ProgramsFilterInterface, ProgramsFilterModel } from '../../models/webinar-course.model';
import { BehaviorSubject } from 'rxjs';
import { RecursiveLinkParamInterface } from '../menu/menu.model';
import { ProgramFilterInterface, RecursiveMatSelectInterface } from '../../models/filter.model';
import { ProgramService } from '../../services/api/program.service';
import { STATUS_TOPIC } from '../../constants/menu.constant';

@Injectable({
    providedIn: 'root',
})
export class ProgramFilterService {
    filterParamSubject = new BehaviorSubject<ProgramsFilterModel>(
        new ProgramsFilterModel({} as ProgramsFilterInterface),
    );
    filterNavbarDataSubject = new BehaviorSubject<RecursiveLinkParamInterface>({} as RecursiveLinkParamInterface);
    topicsSubject = new BehaviorSubject<ProgramFilterInterface>({} as ProgramFilterInterface);
    isFilterReadySubject = new BehaviorSubject<boolean>(false);
    areIdsReadySubject = new BehaviorSubject<boolean>(false);
    updateClassesIdSubject = new BehaviorSubject<string | null>(null);
    eInvoicingIdSubject = new BehaviorSubject<string | null>(null);
    incomeTaxIdSubject = new BehaviorSubject<string | null>(null);
    formsIdSubject = new BehaviorSubject<string | null>(null);
    sstIdSubject = new BehaviorSubject<string | null>(null);

    filterParam$ = this.filterParamSubject.asObservable();
    topics$ = this.topicsSubject.asObservable();
    isFilterReady$ = this.isFilterReadySubject.asObservable();
    areIdsReady$ = this.areIdsReadySubject.asObservable();
    updateClassesId$ = this.updateClassesIdSubject.asObservable();
    eInvoicingId$ = this.eInvoicingIdSubject.asObservable();
    incomeTaxId$ = this.incomeTaxIdSubject.asObservable();
    formsId$ = this.formsIdSubject.asObservable();
    sstId$ = this.sstIdSubject.asObservable();

    constructor(private programService: ProgramService) {}

    getForm(): ProgramsFilterModel {
        return this.filterParamSubject.getValue();
    }

    setFormValues(values: ProgramsFilterModel): void {
        this.filterParamSubject.next(values);
        this.isFilterReadySubject.next(true);
    }

    fetchTopics(): void {
        this.programService.getTopicFilterListing().subscribe({
            next: (topics) => {
                this.topicsSubject.next(topics);
                this.extractIds(topics);
            },
            error: (err) => {
                console.error('Failed to fetch topics:', err);
            },
        });
    }

    getFilterData(id: string, data: RecursiveMatSelectInterface[]): RecursiveMatSelectInterface | undefined {
        const searchRecursive = (items: RecursiveMatSelectInterface[]): RecursiveMatSelectInterface | undefined => {
            for (const item of items) {
                if (item.id === id) {
                    return item;
                }
                if (item.children?.length) {
                    const found = searchRecursive(item.children);

                    if (found) {
                        return found;
                    }
                }
            }

            return undefined;
        };

        return searchRecursive(data);
    }

    private extractIds(data: ProgramFilterInterface): void {
        const topics = data.programTopics || [];

        const topicMap: { key: keyof typeof STATUS_TOPIC; subject: BehaviorSubject<string | null> }[] = [
            { key: 'updateClasses', subject: this.updateClassesIdSubject },
            { key: 'eInvoicing', subject: this.eInvoicingIdSubject },
            { key: 'incomeTax', subject: this.incomeTaxIdSubject },
            { key: 'forms', subject: this.formsIdSubject },
            { key: 'sst', subject: this.sstIdSubject },
        ];

        topicMap.forEach(({ key, subject }) => {
            const match = topics.find((topic) => topic.name.toLowerCase() === STATUS_TOPIC[key].toLowerCase());
            subject.next(match?.id ?? null);
        });

        this.areIdsReadySubject.next(true);
    }
}
